import React, { useState, useMemo, useEffect, useContext } from "react"
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  Table,
  Button,
  Input,
  Label,
  Badge,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Spinner,
} from "reactstrap"
import classnames from "classnames"
import Select from "react-select"
import Flatpickr from "react-flatpickr"
import Swal from "sweetalert2"
import Breadcrumb from "components/Common/Breadcrumb"
import { SessionContext } from "context/sessionContext"
import { sessionApi } from "apis/SessionApi"
import { classApi } from "apis/ClassListApi"
import { feePaymentApi } from "apis/FeePaymentApi"

const selectStyles = {
  menu: provided => ({ ...provided, backgroundColor: "#fff", zIndex: 1000 }),
  menuList: provided => ({
    ...provided,
    backgroundColor: "#fff",
    maxHeight: "200px",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? "#f8f9fa" : "#fff",
    color: "#000",
    cursor: "pointer",
  }),
}

const SETTLEMENT_STATUS_OPTIONS = [
  { value: "All", label: "All" },
  { value: "Under Process", label: "Under Process" },
  { value: "Paid", label: "Paid" },
]

const formatDate = date => {
  if (!date) return ""
  const yyyy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, "0")
  const dd = String(date.getDate()).padStart(2, "0")
  return `${yyyy}-${mm}-${dd}`
}

const FeePaymentReport = () => {
  document.title = "Fee Payment Report"

  const { Session } = useContext(SessionContext)

  // Tabs: "All", "Success", "Failed"
  const [activeTab, setActiveTab] = useState("All")

  // Filter States
  const [sessions, setSessions] = useState([])
  const [selectedSession, setSelectedSession] = useState(null)

  const [classOptions, setClassOptions] = useState([])
  const [selectedClass, setSelectedClass] = useState(null)

  const [selectedStatus, setSelectedStatus] = useState(null)
  const [fromDate, setFromDate] = useState(null)
  const [toDate, setToDate] = useState(null)
  const [searchQuery, setSearchQuery] = useState("")

  const [exportDropdownOpen, setExportDropdownOpen] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  
  // API Data States
  const [studentsData, setStudentsData] = useState([])
  const [loading, setLoading] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [totalPages, setTotalPages] = useState(1)
  const [counts, setCounts] = useState({ All: 0, Paid: 0, "Under Process": 0 })
  const [settlementStatuses, setSettlementStatuses] = useState({})
  const [markingPaid, setMarkingPaid] = useState({})
  const [expandedRows, setExpandedRows] = useState({})

  // ── Fetch Academic Sessions ───────────────────────────────────────────────
  useEffect(() => {
    const fetchSessions = async () => {
      try {
        const res = await sessionApi.GetSession()
        if (res?.data?.session && Array.isArray(res.data.session)) {
          const mapped = res.data.session.map(s => ({
            value: s.id,
            label: s.session_name,
          }))
          setSessions(mapped)

          // Auto-select session from context / sessionStorage
          const activeSessionId = Session || sessionStorage.getItem("SessionId")
          if (activeSessionId) {
            const activeSession = mapped.find(s => String(s.value) === String(activeSessionId))
            if (activeSession) {
              setSelectedSession(activeSession)
            }
          } else if (mapped.length > 0) {
            // Default to first if none active
            setSelectedSession(mapped[0])
          }
        }
      } catch (err) {
        console.error("Failed to fetch sessions:", err)
      }
    }
    fetchSessions()
  }, [Session])

  // ── Fetch Classes/Sections ────────────────────────────────────────────────
  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const res = await classApi.getAllClass()
        if (res?.data?.section_list && Array.isArray(res.data.section_list)) {
          const unique = res.data.section_list
            .map(cl => ({
              value: cl.UID,
              label: `${cl.st_class.toUpperCase()} - ${cl.st_sec.toUpperCase()}`,
              st_class: cl.st_class,
              st_sec: cl.st_sec,
            }))
            .filter(
              (item, index, arr) =>
                index === arr.findIndex(d => d.label === item.label)
            )
          setClassOptions(unique)
        }
      } catch (err) {
        console.error("Failed to fetch classes:", err)
      }
    }
    fetchClasses()
  }, [])

  // ── Fetch Fee Payments from API ───────────────────────────────────────────
  useEffect(() => {
    const fetchFeePayments = async () => {
      setLoading(true)
      try {
        const payload = {
          session_id: selectedSession ? parseInt(selectedSession.value, 10) : 0,
          st_class: selectedClass ? selectedClass.st_class : "",
          st_sec: selectedClass ? selectedClass.st_sec : "",
          search: searchQuery,
          settlement_status: selectedStatus ? selectedStatus.value : activeTab,
          from_date: formatDate(fromDate),
          to_date: formatDate(toDate),
          page: currentPage,
          page_size: 10,
        }

        const res = await feePaymentApi.getFeePaymentList(payload)
        if (res?.data?.status === 200) {
          setStudentsData(res.data.students || [])
          setTotalPages(res.data.total_pages || 1)
          if (res.data.summary) {
            setCounts({
              All: res.data.summary.all_count || 0,
              Paid: res.data.summary.paid_count || 0,
              "Under Process": res.data.summary.under_process_count || 0,
            })
          }
        } else {
          setStudentsData([])
          setTotalPages(1)
        }
      } catch (err) {
        console.error("Failed to fetch fee payments:", err)
        setStudentsData([])
        setTotalPages(1)
      } finally {
        setLoading(false)
      }
    }

    fetchFeePayments()
  }, [
    selectedSession,
    selectedClass,
    selectedStatus,
    fromDate,
    toDate,
    searchQuery,
    activeTab,
    currentPage,
  ])

  // ── Handle Export ────────────────────────────────────────────────────────
  const handleExport = async format => {
    setIsExporting(true)
    try {
      const payload = {
        session_id: selectedSession ? parseInt(selectedSession.value, 10) : 0,
        st_class: selectedClass ? selectedClass.st_class : "",
        st_sec: selectedClass ? selectedClass.st_sec : "",
        search: searchQuery,
        settlement_status: selectedStatus ? selectedStatus.value : activeTab,
        from_date: formatDate(fromDate),
        to_date: formatDate(toDate),
        export_format: format,
      }

      const res = await feePaymentApi.exportFeePaymentReport(payload)

      // If the response is JSON (usually an error message), don't download it
      if (res.data && res.data.type === "application/json") {
        const text = res.data.text ? await res.data.text() : await new Promise(r => {
          const reader = new FileReader()
          reader.onload = () => r(reader.result)
          reader.readAsText(res.data)
        })
        try {
          const errorJson = JSON.parse(text)
          Swal.fire("Error", errorJson.msg || "Failed to export report.", "error")
        } catch (e) {
          Swal.fire("Error", "Failed to export report.", "error")
        }
        return
      }

      const fileType =
        format === "excel"
          ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          : "application/pdf"
      const fileExtension = format === "excel" ? "xlsx" : "pdf"

      const blob = new Blob([res.data], { type: fileType })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", `Fee_Payment_Report_${formatDate(new Date())}.${fileExtension}`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error("Export failed:", err)
      if (
        err.response?.data &&
        err.response.data instanceof Blob &&
        err.response.data.type === "application/json"
      ) {
        try {
          const text = err.response.data.text
            ? await err.response.data.text()
            : await new Promise(r => {
                const reader = new FileReader()
                reader.onload = () => r(reader.result)
                reader.readAsText(err.response.data)
              })
          const errorJson = JSON.parse(text)
          Swal.fire("Error", errorJson.msg || "Failed to export report.", "error")
          return
        } catch (e) {
          // fallback
        }
      }
      Swal.fire("Error", "Failed to export report.", "error")
    } finally {
      setIsExporting(false)
    }
  }

  // ── Handle Mark as Paid ───────────────────────────────────────────────────
  const handleMarkPaid = async (referenceNo, rowId) => {
    const result = await Swal.fire({
      title: "Mark as Paid?",
      text: `Are you sure you want to mark payment ${referenceNo} as Paid?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, mark as Paid",
    })

    if (!result.isConfirmed) return

    setMarkingPaid(prev => ({ ...prev, [rowId]: true }))
    try {
      const payload = {
        session_id: selectedSession ? parseInt(selectedSession.value, 10) : 0,
        payment_reference: referenceNo,
      }
      const res = await feePaymentApi.markFeePaymentPaid(payload)
      if (res?.data?.status === 200) {
        if (res.data.warning) {
          Swal.fire("Warning", res.data.msg || "This payment is already marked as Paid.", "warning")
        } else {
          Swal.fire("Success", res.data.msg || "Payment marked as Paid successfully.", "success")
        }
        setSettlementStatuses(prev => ({
          ...prev,
          [rowId]: "Paid",
        }))
      } else {
        Swal.fire("Error", res?.data?.msg || "Failed to mark payment as Paid.", "error")
      }
    } catch (err) {
      console.error("Mark as Paid failed:", err)
      Swal.fire("Error", err?.response?.data?.msg || "Failed to mark payment as Paid.", "error")
    } finally {
      setMarkingPaid(prev => ({ ...prev, [rowId]: false }))
    }
  }

  // ── Toggle Row Expand/Collapse ──────────────────────────────────────────
  const toggleRowExpand = rowId => {
    setExpandedRows(prev => ({
      ...prev,
      [rowId]: !prev[rowId],
    }))
  }

  // ── Map response data to table structure ──────────────────────────────────
  const flatTableData = useMemo(() => {
    const rows = []
    studentsData.forEach(student => {
      if (student.quarters && student.quarters.length > 0) {
        student.quarters.forEach(q => {
          if (q.transactions && q.transactions.length > 0) {
            q.transactions.forEach(t => {
              rows.push({
                id: `${student.st_code}-${q.quarter}-${t.transaction_id || t.reference_no}`,
                studentName: student.st_name,
                studentCode: student.st_code,
                classSec: `${student.st_class} - ${student.st_sec}`,
                quarters: q.quarter,
                amount: `₹${t.transaction_amount?.toLocaleString() || "0"}`,
                referenceNo: t.reference_no || "-",
                receiptNo: t.receipt_no || "-",
                status: t.payment_status || "Pending",
                mode: t.payment_mode || "-",
                transactionId: t.transaction_id || "-",
                date: t.payment_date || "-",
                feeBreakup: q.fee_breakup || [],
                totalAmount: q.amount || t.transaction_amount || 0,
                settlementStatus: t.settlement_status || q.settlement_status || "",
              })
            })
          } else {
            rows.push({
              id: `${student.st_code}-${q.quarter}-none`,
              studentName: student.st_name,
              studentCode: student.st_code,
              classSec: `${student.st_class} - ${student.st_sec}`,
              quarters: q.quarter,
              amount: `₹${q.amount?.toLocaleString() || "0"}`,
              referenceNo: "-",
              receiptNo: "-",
              status: q.status || "Pending",
              mode: "-",
              transactionId: "-",
              date: "-",
              feeBreakup: q.fee_breakup || [],
              totalAmount: q.amount || 0,
              settlementStatus: q.settlement_status || "",
            })
          }
        })
      } else {
        rows.push({
          id: `${student.st_code}-none`,
          studentName: student.st_name,
          studentCode: student.st_code,
          classSec: `${student.st_class} - ${student.st_sec}`,
          quarters: "-",
          amount: "-",
          referenceNo: "-",
          receiptNo: "-",
          status: "Pending",
          mode: "-",
          transactionId: "-",
          date: "-",
          feeBreakup: [],
          totalAmount: 0,
          settlementStatus: "",
        })
      }
    })

    // Filter rows based on active tab to ensure only matching statuses are shown
    if (activeTab === "Paid") {
      return rows.filter(row => row.settlementStatus === "Paid" || row.status === "Success")
    }
    if (activeTab === "Under Process") {
      return rows.filter(row => row.settlementStatus === "Under Process" || row.settlementStatus === "" || row.status !== "Success")
    }
    // "All" tab shows everything
    return rows
  }, [studentsData, activeTab])

  // Helper to get status badges
  const getStatusBadge = status => {
    let color = "warning"
    if (status === "Success") color = "success"
    if (status === "Failed") color = "danger"
    return (
      <Badge color={color} className="font-size-12 px-3 py-2" pill>
        {status}
      </Badge>
    )
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb title="" breadcrumbItem="Fee Payment Report" />

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  {/* Summary Tabs */}
                  <Row className="mb-3">
                    <Col xs={12}>
                      <Nav tabs className="nav-tabs-custom">
                        {[
                          { key: "All", label: "All Payments", color: "primary" },
                          { key: "Paid", label: "Paid", color: "success" },
                          { key: "Under Process", label: "Under Process", color: "warning" },
                        ].map(tab => (
                          <NavItem key={tab.key}>
                            <NavLink
                              style={{ cursor: "pointer" }}
                              className={classnames({ active: activeTab === tab.key })}
                              onClick={() => {
                                setActiveTab(tab.key)
                                setCurrentPage(1)
                                setSelectedStatus(null) // clear select dropdown status when switching tabs
                              }}
                            >
                              {tab.label}
                              <Badge className="ms-2" color={tab.color}>
                                {counts[tab.key] || 0}
                              </Badge>
                            </NavLink>
                          </NavItem>
                        ))}
                      </Nav>
                    </Col>
                  </Row>

                  {/* Horizontal Filter Bar */}
                  <Row className="mb-4 g-3 align-items-end">
                    <Col sm={6} md={2}>
                      <Label className="form-label font-size-13 text-muted">Academic Session</Label>
                      <Select
                        value={selectedSession}
                        onChange={val => {
                          setSelectedSession(val)
                          setCurrentPage(1)
                        }}
                        options={sessions}
                        placeholder="Session..."
                        styles={selectStyles}
                        isClearable
                      />
                    </Col>
                    <Col sm={6} md={2}>
                      <Label className="form-label font-size-13 text-muted">Class / Section</Label>
                      <Select
                        value={selectedClass}
                        onChange={val => {
                          setSelectedClass(val)
                          setCurrentPage(1)
                        }}
                        options={classOptions}
                        placeholder="Class..."
                        styles={selectStyles}
                        isClearable
                      />
                    </Col>
                    {activeTab === "All" && (
                    <Col sm={6} md={2}>
                      <Label className="form-label font-size-13 text-muted">Status</Label>
                      <Select
                        value={selectedStatus}
                        onChange={val => {
                          setSelectedStatus(val)
                          setCurrentPage(1)
                        }}
                        options={SETTLEMENT_STATUS_OPTIONS}
                        placeholder="Status..."
                        styles={selectStyles}
                        isClearable
                      />
                    </Col>
                    )}
                    <Col sm={6} className="col-md">
                      <Label className="form-label font-size-13 text-muted">From Date</Label>
                      <Flatpickr
                        className="form-control d-block"
                        placeholder="From Date"
                        options={{
                          dateFormat: "Y-m-d",
                        }}
                        value={fromDate}
                        onChange={dates => {
                          setFromDate(dates[0] || null)
                          setCurrentPage(1)
                        }}
                      />
                    </Col>
                    <Col sm={6} className="col-md">
                      <Label className="form-label font-size-13 text-muted">To Date</Label>
                      <Flatpickr
                        className="form-control d-block"
                        placeholder="To Date"
                        options={{
                          dateFormat: "Y-m-d",
                        }}
                        value={toDate}
                        onChange={dates => {
                          setToDate(dates[0] || null)
                          setCurrentPage(1)
                        }}
                      />
                    </Col>
                    <Col sm={12} md={3} className="text-md-end">
                      <div className="d-flex align-items-center justify-content-md-end justify-content-start gap-2">
                        <div className="position-relative flex-grow-1" style={{ maxWidth: "260px" }}>
                          <Input
                            type="text"
                            placeholder="Search student, code, reference..."
                            value={searchQuery}
                            onChange={e => {
                              setSearchQuery(e.target.value)
                              setCurrentPage(1)
                            }}
                            style={{ paddingRight: "30px", borderRadius: "4px" }}
                          />
                          {searchQuery && (
                            <i
                              className="bx bx-x position-absolute"
                              onClick={() => {
                                setSearchQuery("")
                                setCurrentPage(1)
                              }}
                              style={{
                                right: "10px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                cursor: "pointer",
                                fontSize: "18px",
                                color: "#74788d",
                                zIndex: 10,
                              }}
                            />
                          )}
                        </div>
                        <Dropdown
                          isOpen={exportDropdownOpen}
                          toggle={() => setExportDropdownOpen(!exportDropdownOpen)}
                        >
                          <DropdownToggle
                            color="primary"
                            className="btn btn-primary"
                            caret
                            disabled={isExporting}
                          >
                            {isExporting ? (
                              <>
                                <Spinner size="sm" className="me-1" /> Exporting...
                              </>
                            ) : (
                              <>
                                <i className="bx bx-download me-1" /> Export
                              </>
                            )}
                          </DropdownToggle>
                          <DropdownMenu end>
                            <DropdownItem onClick={() => handleExport("excel")}>
                              <i className="bx bx-file me-2 text-success" /> Export Excel
                            </DropdownItem>
                            <DropdownItem onClick={() => handleExport("pdf")}>
                              <i className="bx bxs-file-pdf me-2 text-danger" /> Export PDF
                            </DropdownItem>
                          </DropdownMenu>
                        </Dropdown>
                      </div>
                    </Col>
                  </Row>

                  {/* Responsive Table */}
                  <div className="table-responsive">
                    {loading ? (
                      <div className="text-center py-5">
                        <Spinner color="primary" />
                        <p className="mt-2 text-muted">Loading fee payments...</p>
                      </div>
                    ) : (
                      <Table className="table align-middle table-nowrap table-hover mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Student</th>
                            <th>Class</th>
                            <th>Quarters</th>
                            <th>Amount</th>
                            <th>Reference</th>
                            <th>Receipt</th>
                            <th>Payment Status</th>
                            <th>Payment Mode</th>
                            <th>Transaction ID</th>
                            <th>Payment Date</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {flatTableData.length > 0 ? (
                            flatTableData.map(item => (
                              <React.Fragment key={item.id}>
                                <tr>
                                  <td>
                                    <div>
                                      <h5 className="font-size-14 mb-1 text-dark">
                                        {item.studentName}
                                      </h5>
                                      <p className="text-muted font-size-12 mb-0">
                                        Code: {item.studentCode}
                                      </p>
                                    </div>
                                  </td>
                                  <td>
                                    <span className="font-size-13 text-uppercase">{item.classSec}</span>
                                  </td>
                                  <td>{item.quarters}</td>
                                  <td className="font-weight-medium">{item.amount}</td>
                                  <td>
                                    <span className="text-muted font-size-12">{item.referenceNo}</span>
                                  </td>
                                  <td>{item.receiptNo}</td>
                                  <td>{getStatusBadge(item.status)}</td>
                                  <td>{item.mode}</td>
                                  <td>
                                    <code className="text-info">{item.transactionId}</code>
                                  </td>
                                  <td>{item.date}</td>
                                  <td>
                                    <div className="d-flex align-items-center gap-2">
                                      {item.feeBreakup && item.feeBreakup.length > 0 && (
                                        <span
                                          onClick={() => toggleRowExpand(item.id)}
                                          style={{
                                            cursor: "pointer",
                                            width: "28px",
                                            height: "28px",
                                            borderRadius: "6px",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            background: expandedRows[item.id]
                                              ? "rgba(85, 110, 230, 0.1)"
                                              : "rgba(116, 120, 141, 0.08)",
                                            transition: "all 0.2s ease",
                                          }}
                                          title={expandedRows[item.id] ? "Collapse" : "View Fee Breakup"}
                                        >
                                          <i
                                            className={`mdi ${
                                              expandedRows[item.id]
                                                ? "mdi-chevron-up"
                                                : "mdi-chevron-down"
                                            }`}
                                            style={{
                                              fontSize: "18px",
                                              color: expandedRows[item.id] ? "#556ee6" : "#74788d",
                                              transition: "all 0.2s ease",
                                            }}
                                          />
                                        </span>
                                      )}
                                      {settlementStatuses[item.id] === "Paid" || item.settlementStatus === "Paid" ? (
                                        <Button
                                          color="success"
                                          size="sm"
                                          disabled
                                        >
                                          Paid
                                        </Button>
                                      ) : item.referenceNo && item.referenceNo !== "-" ? (
                                        <Button
                                          color="primary"
                                          size="sm"
                                          disabled={markingPaid[item.id]}
                                          onClick={() => handleMarkPaid(item.referenceNo, item.id)}
                                        >
                                          {markingPaid[item.id] ? (
                                            <Spinner size="sm" />
                                          ) : (
                                            "Mark as Paid"
                                          )}
                                        </Button>
                                      ) : (
                                        !item.feeBreakup?.length && <span className="text-muted">-</span>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                                {/* Expanded Fee Breakup Row */}
                                {expandedRows[item.id] && item.feeBreakup && item.feeBreakup.length > 0 && (
                                  <tr>
                                    <td colSpan="11" className="p-0 border-0">
                                      <div
                                        style={{
                                          background: "linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%)",
                                          borderLeft: "4px solid #556ee6",
                                          borderRadius: "0 0 8px 8px",
                                          padding: "20px 24px",
                                          margin: "0 12px 8px 12px",
                                          boxShadow: "0 2px 8px rgba(85, 110, 230, 0.08)",
                                        }}
                                      >
                                        <div className="d-flex align-items-center mb-3">
                                          <div
                                            style={{
                                              width: "32px",
                                              height: "32px",
                                              borderRadius: "8px",
                                              background: "rgba(85, 110, 230, 0.1)",
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              marginRight: "12px",
                                            }}
                                          >
                                            <i className="bx bx-receipt text-primary font-size-16" />
                                          </div>
                                          <div>
                                            <h6 className="mb-0 font-size-14 text-dark">
                                              Fee Breakup
                                            </h6>
                                          </div>
                                        </div>
                                        <div style={{ display: "flex", flexWrap: "wrap", gap: "16px" }}>
                                          {item.feeBreakup.map((quarterBreakup, qIdx) => (
                                            <div key={qIdx} style={{ flex: "1 1 calc(50% - 8px)", minWidth: "280px", maxWidth: "300px" }}>
                                              <span
                                                className="font-size-12 fw-semibold mb-2 d-inline-block"
                                                style={{
                                                  background: "rgba(85, 110, 230, 0.12)",
                                                  color: "#556ee6",
                                                  padding: "3px 10px",
                                                  borderRadius: "4px",
                                                }}
                                              >
                                                {quarterBreakup.quarter} • {quarterBreakup.items ? quarterBreakup.items.length : 0} fee component{quarterBreakup.items && quarterBreakup.items.length > 1 ? "s" : ""}
                                              </span>
                                              <div
                                                style={{
                                                  background: "#fff",
                                                  borderRadius: "8px",
                                                  overflow: "hidden",
                                                  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                                                  marginTop: "8px",
                                                }}
                                              >
                                                <Table className="table table-sm mb-0" style={{ marginBottom: 0 }}>
                                                  <thead>
                                                    <tr
                                                      style={{
                                                        background: "linear-gradient(135deg, #556ee6 0%, #6b7eff 100%)",
                                                      }}
                                                    >
                                                      <th
                                                        className="text-white font-size-12 py-2 ps-3"
                                                        style={{ fontWeight: 600, border: "none", width: "50px" }}
                                                      >
                                                        #
                                                      </th>
                                                      <th
                                                        className="text-white font-size-12 py-2"
                                                        style={{ fontWeight: 600, border: "none" }}
                                                      >
                                                        Fee Name
                                                      </th>
                                                      <th
                                                        className="text-white font-size-12 py-2 pe-3 text-end"
                                                        style={{ fontWeight: 600, border: "none" }}
                                                      >
                                                        Amount
                                                      </th>
                                                    </tr>
                                                  </thead>
                                                  <tbody>
                                                    {(quarterBreakup.items || []).map((fee, idx) => (
                                                      <tr
                                                        key={idx}
                                                        style={{
                                                          background: idx % 2 === 0 ? "#fff" : "#f8f9fa",
                                                          transition: "background 0.2s",
                                                        }}
                                                      >
                                                        <td
                                                          className="py-2 ps-3 font-size-13"
                                                          style={{ border: "none", color: "#74788d" }}
                                                        >
                                                          {idx + 1}
                                                        </td>
                                                        <td
                                                          className="py-2 font-size-13"
                                                          style={{ border: "none", color: "#495057" }}
                                                        >
                                                          {fee.fee_name}
                                                        </td>
                                                        <td
                                                          className="py-2 pe-3 text-end font-size-13"
                                                          style={{ border: "none", color: "#495057" }}
                                                        >
                                                          ₹{fee.amount?.toLocaleString() || "0"}
                                                        </td>
                                                      </tr>
                                                    ))}
                                                  </tbody>
                                                  <tfoot>
                                                    <tr
                                                      style={{
                                                        borderTop: "2px solid #e9ecef",
                                                        background: "#f8f9ff",
                                                      }}
                                                    >
                                                      <td
                                                        colSpan="2"
                                                        className="py-2 ps-3 text-end font-size-13 fw-bold"
                                                        style={{ border: "none", color: "#556ee6" }}
                                                      >
                                                        Total Amount
                                                      </td>
                                                      <td
                                                        className="py-2 pe-3 text-end font-size-14 fw-bold"
                                                        style={{ border: "none", color: "#556ee6" }}
                                                      >
                                                        ₹{quarterBreakup.amount?.toLocaleString() || "0"}
                                                      </td>
                                                    </tr>
                                                  </tfoot>
                                                </Table>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="11" className="text-center py-4 text-muted">
                                No matching payments found.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    )}
                  </div>

                  {/* Pagination Section */}
                  {totalPages > 1 && (
                    <Row className="mt-4">
                      <Col sm={12} className="d-flex justify-content-center">
                        <div className="pagination pagination-rounded justify-content-end mb-2">
                          <Button
                            color="light"
                            className="btn-rounded me-2"
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage(prev => prev - 1)}
                          >
                            <i className="mdi mdi-chevron-left" />
                          </Button>
                          {[...Array(totalPages)].map((_, i) => (
                            <Button
                              key={i}
                              color={currentPage === i + 1 ? "primary" : "light"}
                              className={classnames("btn-rounded me-2", {
                                active: currentPage === i + 1,
                              })}
                              onClick={() => setCurrentPage(i + 1)}
                            >
                              {i + 1}
                            </Button>
                          ))}
                          <Button
                            color="light"
                            className="btn-rounded"
                            disabled={currentPage === totalPages}
                            onClick={() => setCurrentPage(prev => prev + 1)}
                          >
                            <i className="mdi mdi-chevron-right" />
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default FeePaymentReport
